import { InfectiousSymptom, Photo, PatientEncounter, HealthHistoryTypes, Patient } from "../../../../resources/types";
import { mockEncounter01 } from "../../../../util/printable-obschart/mocks/mockEncounter01";
import { TestRecord_86b0hegk5 } from "../../../../util/printable-obschart/mocks/86b0hegk5";
import { TestRecord_86b0hejq8 } from "../../../../util/printable-obschart/mocks/86b0hejq8";

const createMockEncounters = (count: number): PatientEncounter[] => {

  const availableMockEncounters = [
    mockEncounter01,
    TestRecord_86b0hegk5,
    TestRecord_86b0hejq8,
  ] as const;
  /** randomly select one of the mock encounters */
  const baseMockEncounter = (() => {
    const random = Math.random();
    return availableMockEncounters[Math.floor(random * availableMockEncounters.length)];
  })();

  return Array.from({ length: count }, (_, i) => {
    // Helper function to ensure string values match their enum types
    const ensureEnumValue = <T extends string>(value: string | undefined, validValues: readonly T[]): T | undefined => {
      return value && validValues.includes(value as T) ? value as T : undefined;
    };

    // Cast the base encounter to PatientEncounter to access its fields safely
    const baseEncounter = baseMockEncounter as unknown as PatientEncounter;

    // Ensure healthHistory has the correct type
    const healthHistory = baseEncounter.healthHistory?.map((item) => ({
      type: item.type as HealthHistoryTypes,
      details: item.details
    })) || [];

    // Ensure previousOperations has the correct when type
    const previousOperations = baseEncounter.previousOperations?.map((op) => ({
      type: op.type,
      when: op.when as "lt1month" | "lt6months" | "gt6months" | undefined
    })) || [];

    return {
      ...baseEncounter,
      id: `mock-encounter-${i}`,
      arrived: Date.now() - (i * 1000 * 60 * 30), // 30 minutes apart
      patient: {
        ...baseEncounter.patient,
        firstName: `Patient${i}`,
        lastName: `Test${i}`,
        gender: "Man",
        id: `patient-${i}`,
        dateOfBirth: "2000-01-01",
        allergies: [],
        healthHistory: [],
        healthcareDirective: "no" as const,
        regularMeds: "no" as const,
        medicareNoPos: "1"
      },
      status: i % 3 === 0 ? "seen" : i % 3 === 1 ? "triaged" : "submitted",
      nextObsDue: Date.now() + (i * 1000 * 60 * 15), // 15 minutes apart
      location: {
        id: `loc-${i % 3}`,
        name: `Location ${i % 3}`,
        shortName: `LOC${i % 3}`,
        status: "active"
      },
      riskScore: {
        score: Math.floor(Math.random() * 10),
        risk: Math.floor(Math.random() * 4),
        incomplete: false
      },
      tasks: [],
      notes: [],
      observations: [],
      activities: [],
      delegates: [],
      healthcareDirective: "no" as const,
      regularMeds: "no" as const,
      infectiousSymptoms: [] as InfectiousSymptom[],
      photos: [] as Photo[],
      source: "nurse" as const,
      created: new Date().toISOString(),
      lastModified: new Date().toISOString(),
      healthHistory,
      previousOperations,
      homeSupport: ensureEnumValue(baseEncounter.homeSupport, ["independent", "aid", "assist"] as const),
      alcoholConsumption: ensureEnumValue(baseEncounter.alcoholConsumption, ["none", "less_than_one", "one_to_two", "two_to_five", "five_to_ten", "more_than_ten"] as const),
      tobaccoConsumption: ensureEnumValue(baseEncounter.tobaccoConsumption, ["ex_smoker", "occasional", "little", "moderate", "heavy"] as const)
    };
  });
};

const createMockNextObservations = () => {
  return Array.from({ length: 5 }, (_, i) => ({
    id: `mock-obs-${i}`,
    nextObsDue: Date.now() + (i * 1000 * 60 * 15), // 15 minutes apart
    patient: {
      firstName: `Patient${i}`,
      lastName: `Test${i}`,
      id: `patient-${i}`,
    },
    status: i === 0 ? "triaged" : "submitted",
  }));
};

const createMockAnalytics = () => {
  // Create some random hourly data
  const createHourlyData = () => Array.from({ length: 24 }, () => Math.floor(Math.random() * 10));

  return {
    waittime: {
      num: Math.floor(Math.random() * 60), // Random wait time between 0-60 minutes
      hourly: createHourlyData(),
    },
    patients: {
      num: Math.floor(Math.random() * 50), // Random number of patients between 0-50
      hourly: createHourlyData(),
    },
    triage: {
      num: Math.floor(Math.random() * 10), // Random number of triage cases between 0-10
      hourly: createHourlyData(),
    },
    distress: {
      num: Math.floor(Math.random() * 5), // Random number of distress cases between 0-5
      hourly: createHourlyData(),
    },
    expects: {
      num: Math.floor(Math.random() * 15), // Random number of expected cases between 0-15
      hourly: createHourlyData(),
    },
  };
};

const createMockPatients = (count: number): Patient[] => {
  const availableMockEncounters = [
    mockEncounter01,
    TestRecord_86b0hegk5,
    TestRecord_86b0hejq8,
  ] as const;

  // @ts-ignore
  return Array.from({ length: count }, (_, i) => {
    // Randomly select a base encounter for variety
    const baseEncounter = availableMockEncounters[Math.floor(Math.random() * availableMockEncounters.length)] as unknown as PatientEncounter;

    return {
      id: `patient-${i}`,
      firstName: `Patient${i}`,
      lastName: `Test${i}`,
      email: `patient${i}@example.com`,
      phone: `+61 4${String(i).padStart(8, "0")}`,
      gender: "Man" as const,
      dateOfBirth: "2000-01-01",
      allergies: baseEncounter.patient.allergies || [],
      medicareNo: `123456789${i}`,
      medicareNoPos: "1",
      umrn: `UMRN${String(i).padStart(6, "0")}`,
      anonymous: false,
      unknown: false,
      // Note: identity_id and pushToken are omitted as they are sensitive fields
      // that the real API removes in the after.find hook
    };
  });
};

export {
  createMockEncounters,
  createMockNextObservations,
  createMockAnalytics,
  createMockPatients,
};
